import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { User, Mail, Lock } from "lucide-react";

export default function Register() {
  const { registerWithPassword, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await registerWithPassword(f.name, f.email, f.password);
      toast.success("Account created!");
      nav("/account");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] grid place-items-center py-14 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold text-slate-950">Create your account</h1>
          <p className="text-slate-600 mt-2">Track orders, save favorites & book services faster</p>
        </div>
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-8 space-y-4 shadow-soft">
          <div className="relative">
            <User className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input required placeholder="Full name" value={f.name} onChange={(e) => setF({...f, name: e.target.value})} className="w-full border border-slate-200 rounded-xl pl-11 pr-4 py-3 text-sm" data-testid="register-name" />
          </div>
          <div className="relative">
            <Mail className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({...f, email: e.target.value})} className="w-full border border-slate-200 rounded-xl pl-11 pr-4 py-3 text-sm" data-testid="register-email" />
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input required type="password" placeholder="Password (min 6 chars)" minLength={6} value={f.password} onChange={(e) => setF({...f, password: e.target.value})} className="w-full border border-slate-200 rounded-xl pl-11 pr-4 py-3 text-sm" data-testid="register-password" />
          </div>
          <button disabled={busy} className="w-full bg-blue-900 hover:bg-blue-800 text-white py-3 rounded-xl font-medium disabled:opacity-60" data-testid="register-submit">
            {busy ? "Creating..." : "Create account"}
          </button>
          <div className="relative py-2 text-center text-xs text-slate-400">
            <span className="bg-white px-3 relative z-10">OR</span>
            <div className="absolute inset-x-0 top-1/2 border-t border-slate-200"></div>
          </div>
          <button type="button" onClick={loginWithGoogle} className="w-full border border-slate-300 hover:bg-slate-50 py-3 rounded-xl font-medium flex items-center justify-center gap-2 text-sm" data-testid="register-google">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.3l6.7-6.7C35.3 2.4 30 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.8 6C12.2 13 17.6 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.7c-.6 3-2.2 5.5-4.6 7.2l7.1 5.5c4.1-3.8 6.5-9.4 6.5-16z"/><path fill="#FBBC05" d="M10.3 28.7c-.5-1.5-.8-3.1-.8-4.7s.3-3.2.8-4.7l-7.8-6C.9 16.7 0 20.3 0 24s.9 7.3 2.5 10.7l7.8-6z"/><path fill="#34A853" d="M24 48c6 0 11-2 14.7-5.4l-7.1-5.5c-2 1.4-4.6 2.2-7.6 2.2-6.4 0-11.8-4.3-13.7-10.1l-7.8 6C6.4 42.6 14.6 48 24 48z"/></svg>
            Continue with Google
          </button>
          <div className="text-center text-sm text-slate-600 pt-2">
            Already have an account? <Link to="/login" className="text-blue-900 font-medium">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
